import React from 'react';
import { CaretRight } from '../design-system/icons';

export interface Crumb {
  label: string;
  onClick?: () => void;
  /** Real URL for the crumb: renders an anchor (open-in-new-tab, copy link) that still navigates in-app via onClick. */
  href?: string;
  icon?: React.ComponentType<{ size?: number }>;
  truncate?: boolean;
}

// Shrink factors are weights; the gaps make this a priority order in practice: the campaign
// name absorbs the overflow first, middle crumbs next (never below an ellipsis, so two carets
// can't sit side by side), and the current page never shrinks. It only ellipsizes when it alone
// is wider than the slot. The icon crumb never shrinks.
const SHRINK_NAME = 'min-w-8 shrink-[1000000]';
const SHRINK_MIDDLE = 'min-w-6 shrink-[1000]';
const SHRINK_CURRENT = 'min-w-0 max-w-full shrink';

/**
 * Top-bar breadcrumb, used inside a slot that clips (`overflow-hidden`), so it can never
 * widen the page or run under the actions beside it. Below sm only the Home icon, the
 * immediate parent and the current page show. Every label truncates with a title.
 */
export function CampaignBreadcrumb({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-body-sm-medium">
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        const Icon = c.icon;
        // Home (0), the parent (len-2) and the current page (len-1) stay on narrow screens.
        const hideNarrow = i > 0 && i < crumbs.length - 2;
        const shrink = Icon ? 'shrink-0' : isLast ? SHRINK_CURRENT : c.truncate ? SHRINK_NAME : SHRINK_MIDDLE;
        const visibility = hideNarrow ? 'max-sm:hidden' : '';
        const inner = (
          <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
            {Icon ? <Icon size={16} /> : null}
            <span className={Icon ? 'whitespace-nowrap max-sm:sr-only' : 'truncate'} title={Icon ? undefined : c.label}>
              {c.label}
            </span>
          </span>
        );
        return (
          <React.Fragment key={`${c.label}-${i}`}>
            {i > 0 && (
              <CaretRight size={14} className={`shrink-0 text-muted-foreground/60 ${visibility}`.trim()} />
            )}
            {isLast || !c.onClick ? (
              <span className={`${shrink} ${visibility} text-foreground`.replace(/\s+/g, ' ')}>{inner}</span>
            ) : c.href ? (
              <a
                href={c.href}
                onClick={(e) => {
                  if (!c.onClick || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  c.onClick();
                }}
                className={`${shrink} ${visibility} text-left text-muted-foreground transition-colors hover:text-foreground`.replace(/\s+/g, ' ')}
              >
                {inner}
              </a>
            ) : (
              <button
                type="button"
                onClick={c.onClick}
                className={`${shrink} ${visibility} text-left text-muted-foreground transition-colors hover:text-foreground`.replace(/\s+/g, ' ')}
              >
                {inner}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
